/**
 * Navigation guards for authentication redirects
 * Pure functions — no side effects except throw redirect()
 */

import { redirect } from "@tanstack/react-router";

const PUBLIC_ROUTE_PREFIX = "/auth";
const LOGIN_ROUTE_PREFIX = "/auth/login";

/**
 * Check if a route is public (doesn't require authentication)
 */
export function isPublicRoute(pathname: string): boolean {
  return pathname.startsWith(PUBLIC_ROUTE_PREFIX);
}

/**
 * Check if a route is the login page
 */
export function isLoginPage(pathname: string): boolean {
  return pathname.startsWith(LOGIN_ROUTE_PREFIX);
}

/**
 * Redirect unauthenticated user to login page
 * Throws TanStack Router redirect if user is not logged in on a protected route
 */
export function redirectUnauthenticatedUser({
  isLoggedIn,
  pathname,
  href,
}: {
  isLoggedIn: boolean;
  pathname: string;
  href: string;
}): void {
  if (!isLoggedIn && !isPublicRoute(pathname)) {
    throw redirect({
      to: "/auth/login",
      search: { redirect: href } as never,
    });
  }
}

/**
 * Redirect authenticated user away from login page
 * Throws TanStack Router redirect if user is logged in on login page
 */
export function redirectAuthenticatedFromLogin({
  isLoggedIn,
  pathname,
}: {
  isLoggedIn: boolean;
  pathname: string;
}): void {
  if (isLoggedIn && isLoginPage(pathname)) {
    throw redirect({ to: "/" });
  }
}
